import { ClientCookieManager } from 'dibs-consented-storage';
import { DIBS_SESSION } from './helpers/constants';

// 30 minutes in milliseconds
const SESSION_TIMEOUT = 30 * 60 * 1000;

type CampaignParams = {
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    utm_content: string | null;
    utm_term: string | null;
};

type SessionInfo = {
    sessionId: string;
    lastActivity: number;
    campaign: CampaignParams;
};

/**
 * Generate a unique session ID
 * @returns {string} A unique session ID
 */
function generateSessionId(): string {
    return `${Math.random().toString().slice(2, 14)}.${Date.now()}`;
}

/**
 * Set the session cookie
 * @param {SessionInfo} info - The session info
 */
function setSessionCookie(info: SessionInfo): void {
    const {
        utm_source: s,
        utm_medium: m,
        utm_campaign: ca,
        utm_content: co,
        utm_term: t,
    } = info?.campaign || {};
    const value = `${info.sessionId}|${info.lastActivity}|${JSON.stringify({ s, m, ca, co, t })}`;
    const expires = new Date(Date.now() + SESSION_TIMEOUT);
    ClientCookieManager.set(DIBS_SESSION, value, { expires });
}

/**
 * Get the campaign params from the URL
 * @returns {CampaignParams} The campaign params
 */
function getCampaignParams(): CampaignParams {
    const params = new URLSearchParams(window.location.search);
    return {
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_content: params.get('utm_content'),
        utm_term: params.get('utm_term'),
    };
}

/**
 * Check if the campaign has changed
 * @param {CampaignParams} prev - The previous campaign params from localStorage
 * @param {CampaignParams} curr - The current campaign params from the URL
 * @returns {boolean} True if the campaign has changed, false otherwise
 */
function isNewCampaign(prev: CampaignParams, curr: CampaignParams): boolean {
    // are all the keys in the current camapaign null?
    if (Object.values(curr).every(v => v === null)) {
        // don't start a new session if the current campaign is null
        return false;
    }
    return (
        prev.utm_source !== curr.utm_source ||
        prev.utm_medium !== curr.utm_medium ||
        prev.utm_campaign !== curr.utm_campaign ||
        prev.utm_content !== curr.utm_content ||
        prev.utm_term !== curr.utm_term
    );
}

/**
 * Validate the session info
 * @param {unknown | null} session - The session info from localStorage
 * @returns {boolean} True if the session info is valid, false otherwise
 */
function validateSessionInfo(session: unknown | null): session is SessionInfo {
    if (!session) {
        return false;
    }
    if (!(session as SessionInfo).sessionId) {
        return false;
    }
    if (!(session as SessionInfo).lastActivity) {
        return false;
    }
    return true;
}

/**
 * Get the session info from localStorage
 * @returns {SessionInfo | null} The session info
 */
function getSessionInfo(): SessionInfo | null {
    if (!window.localStorage) {
        return null;
    }
    try {
        const session = JSON.parse(localStorage.getItem(DIBS_SESSION) || 'null');
        if (!validateSessionInfo(session)) {
            return null;
        }
        return session;
    } catch {
        return null;
    }
}

/**
 * Set the session info in localStorage and cookie
 * @param {SessionInfo} info - The session info
 */
function setSessionInfo(info: SessionInfo): void {
    const serializedInfo = JSON.stringify(info);
    try {
        localStorage.setItem(DIBS_SESSION, serializedInfo);
        setSessionCookie(info);
    } catch {
        // silently fail
    }
}

/**
 * Create a new session
 * @param {string} sessionId - The session ID
 * @param {CampaignParams} campaign - The campaign params
 * @returns {SessionInfo} The session info
 */
function createNewSession(sessionId: string, campaign: CampaignParams): SessionInfo {
    return {
        sessionId,
        lastActivity: Date.now(),
        campaign,
    };
}

/**
 * Initialize a new session or update existing and existing session
 */
export function initDibsSession(): void {
    const currentCampaign = getCampaignParams();
    let session = getSessionInfo();
    const isSessionExpired = !session || Date.now() - session.lastActivity > SESSION_TIMEOUT;
    // Check campaign change *only if* there is a valid session
    const isCampaignChanged =
        session && !isSessionExpired && isNewCampaign(session.campaign, currentCampaign);

    if (!session || isSessionExpired) {
        // No session or expired: create entirely new session with new ID
        session = createNewSession(generateSessionId(), currentCampaign);
    } else if (isCampaignChanged) {
        // Existing session, not expired, but campaign changed: Create new session record, reuse existing ID
        session = createNewSession(session.sessionId, currentCampaign);
    } else {
        // Existing session, not expired, campaign same: Just update last activity
        session.lastActivity = Date.now();
    }
    setSessionInfo(session);
}
